import { useEffect, useState } from 'react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { FileViewer, extOf } from './FileViewer';
import { FileVersions } from './FileVersions';
import { FileSignatures } from './FileSignatures';
import { fileBase64 } from '@/lib/fileBytes';
import { getSDK } from '@/store/sdkStore';
import { friendlyError, logSwallowed } from '@/lib/errors';
import { downloadBytes } from '@/lib/printForm';
import { fileExt, mediaKind } from '@/lib/fileName';
import { MediaPlayer } from '@/components/media/MediaPlayer';
import { ImagePreview } from '@/components/media/ImagePreview';

// ============================================================================
// FilePreviewModal — просмотр файла по id. Для xlsx/csv/docx использует
// СЕРВЕРНЫЙ ParseContent (SSoT): xlsx/csv → сетки (грид), docx → текст.
// Остальное — как раньше: текстовые — utf-8, бинарные (img/pdf) — base64.
// ============================================================================

export interface FilePreviewModalProps {
    fileId: string;
    fileName: string;
    onClose: () => void;
}

const TEXT_EXT = ['md', 'txt', 'log', 'json', 'ts', 'tsx', 'js', 'jsx', 'go', 'py', 'sql', 'sh', 'bat', 'ps1', 'yaml', 'yml', 'xml', 'html', 'css', 'bsl', 'ini'];

export function FilePreviewModal({ fileId, fileName, onClose }: FilePreviewModalProps) {
    const [state, setState] = useState<{ base64?: string; text?: string; parsed?: any; error?: string }>({});
    // M4: восстановление версии → перечитать содержимое предпросмотра.
    const [reloadKey, setReloadKey] = useState(0);
    // MIME — первичный признак классификации (mediaKind): webm бывает и
    // голосовым (audio/webm), и видео (video/webm); ошибка — классификация
    // по расширению (просмотр не блокируется, ошибка в журнал).
    const [mime, setMime] = useState('');
    // Пока MIME не получен, webm нельзя уверенно отнести к аудио/видео —
    // показываем «Загрузка…» вместо неверного плеера (видео-окно не «пустое»).
    const [mimeReady, setMimeReady] = useState(false);
    useEffect(() => {
        let alive = true;
        setMimeReady(false);
        getSDK().files.getFileInfo({ id: fileId })
            .then((info) => { if (alive) setMime(String(info.fileType || '')); })
            .catch((e: unknown) => { logSwallowed(e, 'components/viewer/FilePreviewModal.tsx:mime'); return undefined; })
            .finally(() => { if (alive) setMimeReady(true); });
        return () => { alive = false; };
    }, [fileId]);
    // Аудио/видео — ЕДИНЫЙ SSoT проигрыватель (стриминг/Range, скорость,
    // «картинка в картинке»), без скачивания всего файла в память.
    const media = mediaKind(fileName, mime || undefined);
    const waitMime = !mimeReady && !mime && fileExt(fileName) === 'webm';

    useEffect(() => {
        if (media !== 'file') return;
        let alive = true;
        (async () => {
            try {
                const sdk = getSDK();
                const ext = extOf(fileName);
                const parsedExts = ['xlsx', 'xlsm', 'csv', 'tsv', 'docx'];
                if (parsedExts.includes(ext)) {
                    const r: any = await sdk.files.parseContent({ fileId });
                    if (!alive) return;
                    if (r?.error) { setState({ error: r.error }); return; }
                    setState({ parsed: r });
                    return;
                }
                const r = await sdk.files.downloadFile({ id: fileId });
                const b64 = fileBase64(r?.data);
                if (!alive) return;
                if (TEXT_EXT.includes(ext)) {
                    const bin = atob(b64);
                    const bytes = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                    setState({ text: new TextDecoder('utf-8').decode(bytes) });
                } else {
                    setState({ base64: b64 });
                }
            } catch (e: any) {
                if (alive) setState({ error: e?.message || String(e) });
            }
        })();
        return () => { alive = false; };
    }, [fileId, fileName, reloadKey, media]);

    const downloadFile = async () => {
        try {
            const r = await getSDK().files.downloadFile({ id: fileId });
            const b64 = fileBase64(r?.data);
            if (!b64) throw new Error('файл пуст');
            downloadBytes(fileName || 'file', 'application/octet-stream', b64);
        } catch (e: unknown) {
            setState((s) => ({ ...s, error: friendlyError(e) }));
        }
    };

    return (
        <UniversalWindow open title={`Просмотр: ${fileName}`} width="w-[min(760px,94vw)]" height="h-[min(80vh,92vh)]" onClose={onClose}>
            <div className="h-full overflow-auto p-3">
                <FileVersions fileId={fileId} fileName={fileName} onRestored={() => setReloadKey((k) => k + 1)} />
                <FileSignatures fileId={fileId} fileName={fileName} />
                {waitMime && <div className="text-xs text-muted-foreground">Загрузка…</div>}
                {!waitMime && media === 'image' && <ImagePreview fileId={fileId} fileName={fileName} />}
                {!waitMime && (media === 'video' || media === 'audio') && (
                    <div className="mb-2 truncate text-xs text-muted-foreground" title={fileName}>{fileName}</div>
                )}
                {!waitMime && media === 'video' && (
                    <MediaPlayer fileId={fileId} kind="video" name={fileName} onDownload={() => void downloadFile()} />
                )}
                {!waitMime && media === 'audio' && (
                    <MediaPlayer fileId={fileId} kind="audio" name={fileName} onDownload={() => void downloadFile()} />
                )}
                {media === 'file' && state.error && <div className="text-xs text-destructive">{state.error}</div>}
                {media === 'file' && !state.error && !state.base64 && state.text === undefined && state.parsed === undefined && (
                    <div className="text-xs text-muted-foreground">Загрузка…</div>
                )}
                {media === 'file' && state.parsed && <ParsedSheetView parsed={state.parsed} fileName={fileName} />}
                {media === 'file' && (state.base64 !== undefined || state.text !== undefined) && (
                    <FileViewer name={fileName} base64={state.base64} content={state.text} />
                )}
            </div>
        </UniversalWindow>
    );
}

// ParsedSheetView — рендер результата ParseContent (xlsx/csv → сетки, docx → текст).
function ParsedSheetView({ parsed, fileName }: { parsed: any; fileName: string }) {
    if (parsed?.kind === 'docx' || (parsed?.kind === 'text' && parsed?.text !== undefined)) {
        return <pre className="whitespace-pre-wrap text-xs">{parsed.text || '(пусто)'}</pre>;
    }
    const sheets = parsed?.sheets || [];
    if (!sheets.length) return <div className="text-xs text-muted-foreground">Нет данных</div>;
    return (
        <div className="space-y-2">
            {sheets.map((sheet: any, si: number) => (
                <div key={si} className="rounded border">
                    <div className="border-b bg-muted px-2 py-1 text-xs font-medium">{sheet?.name || `Лист ${si + 1}`}</div>
                    <table className="w-full border-collapse text-xs">
                        <tbody>
                            {(sheet?.rows || []).map((row: string[], ri: number) => (
                                <tr key={ri} className="border-b last:border-0">
                                    {row.map((cell, ci) => (
                                        <td key={ci} className={`border-r px-2 py-0.5 last:border-0 ${ri === 0 ? 'bg-muted/60 font-medium' : ''}`}>
                                            {cell ?? ''}
                                        </td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {fileName.endsWith('.xlsx') && <div className="text-[10px] text-muted-foreground">Просмотр до 50 строк на лист (полный файл — скачайте).</div>}
        </div>
    );
}
